import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Brain, Mic, MicOff, Send, Sparkles, Target, Volume2, VolumeX, 
  RefreshCw, BookOpen, Loader2, User, Zap, GraduationCap, Square, PlayCircle,
  Settings, ChevronDown, MessageSquarePlus, History, Trash2, PanelLeftClose, PanelLeft,
  Paperclip, FileText, X
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { ChatSidebar } from '../components/studybuddy/ChatSidebar';
import { MessageList } from '../components/studybuddy/MessageList';
import { QuickActions } from '../components/studybuddy/QuickActions';
// Day-0 tutor-memory capture moved here from Dashboard (Feb 2026 UX pass).
// Triggering it on the StudyBuddy page is more context-relevant — the user
// is literally about to chat with the AI tutor, so capturing "what are you
// studying" right here justifies the popup. Also frees the Dashboard tabs
// from being intercepted on first load.
import Day0TutorMemoryModal from '../components/Day0TutorMemoryModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Voice Character Personas
const VOICE_PERSONAS = [
  { 
    id: 'professor', 
    name: 'Friendly Professor', 
    icon: '👨‍🏫',
    description: 'Warm and encouraging',
    voicePrefs: { pitch: 0.9, rate: 0.95, gender: 'male' }
  },
  { 
    id: 'tutor', 
    name: 'Strict Tutor', 
    icon: '📚',
    description: 'Precise and formal',
    voicePrefs: { pitch: 0.85, rate: 0.9, gender: 'male' }
  },
  { 
    id: 'peer', 
    name: 'Peer Student', 
    icon: '🎓',
    description: 'Casual and relatable',
    voicePrefs: { pitch: 1.1, rate: 1.05, gender: 'female' }
  },
  { 
    id: 'female', 
    name: 'Female Voice', 
    icon: '👩',
    description: 'Clear female voice',
    voicePrefs: { pitch: 1.15, rate: 1.0, gender: 'female' }
  },
  { 
    id: 'male', 
    name: 'Male Voice', 
    icon: '👨',
    description: 'Clear male voice',
    voicePrefs: { pitch: 0.85, rate: 1.0, gender: 'male' }
  },
];

const WELCOME_MESSAGE = {
  role: 'assistant',
  content: `Hi! I'm your AI Study Buddy, powered by Gemini AI. I'm here to help you succeed in your studies!

**What I can do:**
- Explain complex topics in simple terms
- Create personalized quizzes
- Build custom study plans
- Help prepare for exams
- Analyze uploaded documents (PDF, TXT, DOC)
- Answer any questions about your courses

What would you like to work on today?`
};

const StudyBuddyPage = () => {
  const [messages, setMessages] = useState([WELCOME_MESSAGE]);
  const [input, setInput] = useState('');
  const [recording, setRecording] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [isSpeakingNow, setIsSpeakingNow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [voiceInputEnabled, setVoiceInputEnabled] = useState(false);
  const [voiceOutputEnabled, setVoiceOutputEnabled] = useState(true);
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [availableVoices, setAvailableVoices] = useState([]);
  const [selectedPersona, setSelectedPersona] = useState('professor');
  const [voiceSettingsOpen, setVoiceSettingsOpen] = useState(false);
  const [subjects, setSubjects] = useState([]);
  // Focus scope — restricts the AI's grounding context to a single
  // subject (or all knowledge). When set to a subject_id, the backend
  // narrows its retrieval to lectures within that subject so answers
  // are tighter and more relevant. Initial value falls back to the URL
  // `?focus=<subject_id>` param so deep-links from a lecture card land
  // here with the correct scope pre-selected.
  const [searchParams] = useSearchParams();
  const [focusScope, setFocusScope] = useState(() => {
    try {
      const fromUrl = searchParams.get('focus');
      if (fromUrl) {
        localStorage.setItem('istudypro_studybuddy_focus', fromUrl);
        return fromUrl;
      }
      return localStorage.getItem('istudypro_studybuddy_focus') || 'all';
    } catch {
      return 'all';
    }
  });
  // Chat session state
  const [sessions, setSessions] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const saveTimeoutRef = useRef(null);
  const messagesEndRef = useRef(null);
  const recognitionRef = useRef(null);
  const currentUtteranceRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const [voiceMode, setVoiceMode] = useState('none');
  const [transcribingVoice, setTranscribingVoice] = useState(false);
  const [attachedFile, setAttachedFile] = useState(null);
  const [fileAnalyzing, setFileAnalyzing] = useState(false);
  const fileInputRef = useRef(null);

  // Swipe gesture state for session switching
  const swipeTouchRef = useRef({ startX: 0, startY: 0, swiping: false });
  const [swipeHint, setSwipeHint] = useState(null); // { direction: 'left'|'right', sessionName: string }

  const handleChatTouchStart = (e) => {
    swipeTouchRef.current = { 
      startX: e.touches[0].clientX, 
      startY: e.touches[0].clientY,
      swiping: false 
    };
  };

  const handleChatTouchMove = (e) => {
    if (sessions.length <= 1) return;
    const deltaX = e.touches[0].clientX - swipeTouchRef.current.startX;
    const deltaY = Math.abs(e.touches[0].clientY - swipeTouchRef.current.startY);
    // Only horizontal swipe (not vertical scroll)
    if (Math.abs(deltaX) > 30 && deltaY < 60) {
      swipeTouchRef.current.swiping = true;
      const currentIdx = sessions.findIndex(s => s.id === activeSessionId);
      if (deltaX > 0 && currentIdx > 0) {
        setSwipeHint({ direction: 'right', sessionName: sessions[currentIdx - 1].title || 'Previous Chat' });
      } else if (deltaX < 0 && currentIdx < sessions.length - 1) {
        setSwipeHint({ direction: 'left', sessionName: sessions[currentIdx + 1].title || 'Next Chat' });
      } else {
        setSwipeHint(null);
      }
    }
  };

  const handleChatTouchEnd = (e) => {
    if (!swipeTouchRef.current.swiping || sessions.length <= 1) {
      setSwipeHint(null);
      return;
    }
    const deltaX = e.changedTouches[0].clientX - swipeTouchRef.current.startX;
    const currentIdx = sessions.findIndex(s => s.id === activeSessionId);
    
    if (deltaX > 60 && currentIdx > 0) {
      // Swipe right → previous session
      if (navigator.vibrate) navigator.vibrate(15);
      loadSession(sessions[currentIdx - 1].id);
      toast.info(`Switched to: ${sessions[currentIdx - 1].title || 'Previous Chat'}`, { duration: 1500 });
    } else if (deltaX < -60 && currentIdx < sessions.length - 1) {
      // Swipe left → next session
      if (navigator.vibrate) navigator.vibrate(15);
      loadSession(sessions[currentIdx + 1].id);
      toast.info(`Switched to: ${sessions[currentIdx + 1].title || 'Next Chat'}`, { duration: 1500 });
    }
    setSwipeHint(null);
    swipeTouchRef.current.swiping = false;
  };

  useEffect(() => {
    fetchSubjects();
    fetchSessions();
    initializeSpeech();
    initializeVoices();
    
    return () => {
      if (window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchSubjects = async () => {
    try {
      const response = await axios.get(`${API}/subjects`);
      setSubjects(response.data);
    } catch (error) {
      console.error('Error fetching subjects:', error);
    }
  };

  const fetchSessions = async () => {
    try {
      const res = await axios.get(`${API}/chat-sessions`);
      setSessions(res.data || []);
      // Auto-load the most recent session
      if (res.data?.length > 0) {
        loadSession(res.data[0].id);
      }
    } catch (error) {
      console.error('Error fetching sessions:', error);
    } finally {
      setSessionsLoading(false);
    }
  };

  const loadSession = async (sessionId) => {
    try {
      const res = await axios.get(`${API}/chat-sessions/${sessionId}`);
      setActiveSessionId(sessionId);
      if (res.data.messages?.length > 0) {
        setMessages(res.data.messages);
      } else {
        setMessages([WELCOME_MESSAGE]);
      }
      setSidebarOpen(false);
    } catch (error) {
      console.error('Error loading session:', error);
    }
  };

  const createNewChat = async () => {
    try {
      const res = await axios.post(`${API}/chat-sessions`, { title: "New Chat" });
      setActiveSessionId(res.data.id);
      setMessages([WELCOME_MESSAGE]);
      setSessions(prev => [res.data, ...prev]);
      setSidebarOpen(false);
      stopSpeaking();
    } catch (error) {
      console.error('Error creating session:', error);
    }
  };

  const deleteSession = async (sessionId) => {
    try {
      await axios.delete(`${API}/chat-sessions/${sessionId}`);
      setSessions(prev => prev.filter(s => s.id !== sessionId));
      if (activeSessionId === sessionId) {
        setActiveSessionId(null);
        setMessages([WELCOME_MESSAGE]);
      }
    } catch (error) {
      console.error('Error deleting session:', error);
    }
  };

  const saveMessagesDebounced = useCallback((sessionId, msgs) => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(async () => {
      try {
        await axios.put(`${API}/chat-sessions/${sessionId}/messages`, {
          messages: msgs.map(m => ({ role: m.role, content: m.content }))
        });
        // Refresh sessions list to update titles
        const res = await axios.get(`${API}/chat-sessions`);
        setSessions(res.data || []);
      } catch (err) {
        console.error('Auto-save failed:', err);
      }
    }, 1500);
  }, []);

  const initializeVoices = () => {
    if (!('speechSynthesis' in window)) return;

    const loadVoices = () => {
      const voices = window.speechSynthesis.getVoices();
      setAvailableVoices(voices);
      
      // Try to find a good English voice
      const preferredVoice = voices.find(v => 
        v.name.includes('Google') && v.lang.startsWith('en')
      ) || voices.find(v => 
        v.name.includes('Samantha') || v.name.includes('Alex')
      ) || voices.find(v => 
        v.lang.startsWith('en')
      ) || voices[0];
      
      setSelectedVoice(preferredVoice);
    };

    // Voices might load asynchronously
    if (window.speechSynthesis.getVoices().length > 0) {
      loadVoices();
    }
    window.speechSynthesis.onvoiceschanged = loadVoices;
  };

  const initializeSpeech = () => {
    if ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;
      recognitionRef.current.lang = 'en-US';
      
      recognitionRef.current.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setInput(transcript);
        setRecording(false);
        toast.success('Voice captured! Click send or press Enter.', { icon: '🎤' });
      };
      
      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setRecording(false);

        // On network error, fall back to Whisper
        if (event?.error === 'network' || event?.error === 'service-not-allowed') {
          setVoiceMode('whisper');
          toast.info('Switched to Whisper voice mode for better reliability.');
          return;
        }

        if (event.error === 'not-allowed') {
          toast.error('Microphone access denied. Please allow microphone permissions.');
          return;
        }

        if (event.error !== 'no-speech' && event.error !== 'aborted') {
          toast.error('Voice recognition failed. Try again.');
        }
      };
      
      recognitionRef.current.onend = () => {
        setRecording(false);
      };
      
      setVoiceMode('native');
      setVoiceInputEnabled(true);
    } else if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      // Fallback: use MediaRecorder + Whisper API
      setVoiceMode('whisper');
      setVoiceInputEnabled(true);
    }
  };

  // Get the best matching voice for a persona
  const getVoiceForPersona = useCallback((persona) => {
    if (!availableVoices.length) return null;
    
    const prefs = persona.voicePrefs;
    
    // Try to find a voice matching the gender preference
    let matchedVoice = availableVoices.find(v => {
      const nameLower = v.name.toLowerCase();
      if (prefs.gender === 'female') {
        return nameLower.includes('female') || 
               nameLower.includes('samantha') || 
               nameLower.includes('karen') ||
               nameLower.includes('victoria') ||
               nameLower.includes('zira');
      } else {
        return nameLower.includes('male') || 
               nameLower.includes('daniel') || 
               nameLower.includes('david') ||
               nameLower.includes('alex') ||
               nameLower.includes('james');
      }
    });
    
    // Fallback to any English voice
    if (!matchedVoice) {
      matchedVoice = availableVoices.find(v => v.lang.startsWith('en')) || availableVoices[0];
    }
    
    return matchedVoice;
  }, [availableVoices]);

  // Speak text using Web Speech Synthesis
  const speakText = useCallback((text) => {
    if (!('speechSynthesis' in window) || !voiceOutputEnabled) return;

    // Cancel any ongoing speech
    window.speechSynthesis.cancel();

    // Clean text for speech (remove markdown, emojis, etc.)
    const cleanText = text
      .replace(/\*\*/g, '')
      .replace(/•/g, '')
      .replace(/[📚🎯📅🎓💡👋]/g, '')
      .replace(/\n+/g, '. ')
      .trim();

    if (!cleanText) return;

    // Get current persona settings
    const persona = VOICE_PERSONAS.find(p => p.id === selectedPersona) || VOICE_PERSONAS[0];
    const voiceForPersona = getVoiceForPersona(persona);

    const utterance = new SpeechSynthesisUtterance(cleanText);
    utterance.voice = voiceForPersona || selectedVoice;
    utterance.rate = persona.voicePrefs.rate;
    utterance.pitch = persona.voicePrefs.pitch;
    utterance.volume = 1.0;

    utterance.onstart = () => {
      setIsSpeakingNow(true);
    };

    utterance.onend = () => {
      setIsSpeakingNow(false);
      currentUtteranceRef.current = null;
    };

    utterance.onerror = (event) => {
      console.error('Speech synthesis error:', event);
      setIsSpeakingNow(false);
      currentUtteranceRef.current = null;
    };

    currentUtteranceRef.current = utterance;
    window.speechSynthesis.speak(utterance);
  }, [voiceOutputEnabled, selectedVoice, selectedPersona, getVoiceForPersona]);

  // Stop speaking
  const stopSpeaking = () => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      setIsSpeakingNow(false);
      currentUtteranceRef.current = null;
    }
  };

  // Replay last assistant message
  const replayLastMessage = () => {
    const lastAssistantMessage = [...messages].reverse().find(m => m.role === 'assistant');
    if (lastAssistantMessage) {
      speakText(lastAssistantMessage.content);
    }
  };

  const handleSend = async () => {
    if (!input.trim() && !attachedFile) return;

    let fileContext = '';
    const fileName = attachedFile?.name || null;

    // If file attached, upload and analyze first
    if (attachedFile) {
      setFileAnalyzing(true);
      try {
        const formData = new FormData();
        formData.append('file', attachedFile);
        const uploadRes = await axios.post(`${API}/uploads/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        const fileId = uploadRes.data.file_id;

        // Analyze the uploaded file
        const analyzeRes = await axios.post(`${API}/uploads/${fileId}/analyze`);
        const analysis = analyzeRes.data;

        fileContext = `\n\n[ATTACHED DOCUMENT: "${fileName}"]\n`;
        if (analysis.summary) fileContext += `Summary: ${analysis.summary}\n`;
        if (analysis.topics?.length) fileContext += `Key Topics: ${analysis.topics.join(', ')}\n`;
        if (analysis.key_takeaways?.length) fileContext += `Takeaways: ${analysis.key_takeaways.join('; ')}\n`;
      } catch (err) {
        console.error('File analysis failed:', err);
        fileContext = `\n\n[ATTACHED DOCUMENT: "${fileName}" — could not be analyzed. Please ask the user to try again.]`;
      } finally {
        setFileAnalyzing(false);
        setAttachedFile(null);
      }
    }

    const displayContent = fileName
      ? `${input.trim()}\n\n📎 Attached: ${fileName}`
      : input;
    const aiContent = input.trim() + fileContext;

    const userMessage = { role: 'user', content: displayContent };
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setInput('');
    setLoading(true);

    // Stop any ongoing speech
    stopSpeaking();

    // Auto-create session if none active
    let currentSessionId = activeSessionId;
    if (!currentSessionId) {
      try {
        const res = await axios.post(`${API}/chat-sessions`, { title: "New Chat" });
        currentSessionId = res.data.id;
        setActiveSessionId(currentSessionId);
        setSessions(prev => [res.data, ...prev]);
      } catch (e) {
        console.error('Failed to create session:', e);
      }
    }

    try {
      const [progressRes, statsRes] = await Promise.all([
        axios.get(`${API}/progress`).catch(() => ({ data: {} })),
        axios.get(`${API}/dashboard/stats`).catch(() => ({ data: {} }))
      ]);
      
      const focusedSubject = focusScope !== 'all'
        ? subjects.find((s) => s.id === focusScope)
        : null;
      const userContext = {
        subjects: subjects.map(s => s.name),
        total_lectures: statsRes.data.total_lectures || 0,
        study_hours: statsRes.data.total_study_hours || 0,
        progress: progressRes.data,
        // Focus scope — when set, AI prioritizes this subject's content
        focus_subject_id: focusScope !== 'all' ? focusScope : null,
        focus_subject_name: focusedSubject?.name || null,
      };

      const response = await axios.post(`${API}/study-buddy/chat`, {
        messages: updatedMessages.map(m => {
          // For the last user message, use the AI-enriched content
          if (m === userMessage && fileContext) {
            return { role: m.role, content: aiContent };
          }
          return { role: m.role, content: m.content };
        }),
        user_context: userContext
      });

      const aiMessage = response.data;
      const finalMessages = [...updatedMessages, aiMessage];
      setMessages(finalMessages);
      
      // Auto-save to session
      if (currentSessionId) {
        saveMessagesDebounced(currentSessionId, finalMessages);
      }
      
      if (voiceOutputEnabled && aiMessage.content) {
        setTimeout(() => {
          speakText(aiMessage.content);
        }, 100);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = {
        role: 'assistant',
        content: 'I apologize, I\'m having trouble connecting right now. Please try again in a moment.'
      };
      setMessages([...updatedMessages, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleVoice = async () => {
    if (!voiceInputEnabled) {
      toast.error('Voice input not supported in this browser');
      return;
    }

    // Stop any ongoing speech output
    stopSpeaking();

    if (!recording) {
      // === START RECORDING ===
      if (voiceMode === 'native' && recognitionRef.current) {
        // Try native SpeechRecognition first
        try {
          recognitionRef.current.start();
          setRecording(true);
          toast.info('Listening... Speak now', { icon: '🎤' });
        } catch (e) {
          console.error('Native recognition failed, switching to Whisper:', e);
          // Fall back to Whisper mode
          setVoiceMode('whisper');
          startWhisperRecording();
        }
      } else {
        // Use Whisper fallback (MediaRecorder + API)
        startWhisperRecording();
      }
    } else {
      // === STOP RECORDING ===
      if (voiceMode === 'native' && recognitionRef.current) {
        recognitionRef.current.stop();
        setRecording(false);
      } else {
        stopWhisperRecording();
      }
    }
  };

  const startWhisperRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4';
      const recorder = new MediaRecorder(stream, { mimeType });
      audioChunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          audioChunksRef.current.push(e.data);
        }
      };

      recorder.onstop = () => {
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorderRef.current = recorder;
      recorder.start();
      setRecording(true);
      toast.info('Recording... Click mic again to stop and transcribe', { icon: '🎤' });
    } catch (error) {
      console.error('Microphone access error:', error);
      toast.error('Failed to access microphone. Please allow microphone permissions.');
    }
  };

  const stopWhisperRecording = async () => {
    if (!mediaRecorderRef.current || mediaRecorderRef.current.state === 'inactive') {
      setRecording(false);
      return;
    }

    // Stop and wait for final data
    const recorder = mediaRecorderRef.current;
    
    await new Promise((resolve) => {
      recorder.addEventListener('stop', resolve, { once: true });
      recorder.stop();
    });

    setRecording(false);
    
    const chunks = audioChunksRef.current;
    if (chunks.length === 0) {
      toast.error('No audio captured. Try again.');
      return;
    }

    // Send to Whisper API
    setTranscribingVoice(true);
    toast.info('Transcribing with Whisper AI...', { icon: '🎯' });

    try {
      const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4';
      const ext = mimeType === 'audio/webm' ? 'webm' : 'mp4';
      const blob = new Blob(chunks, { type: mimeType });
      const formData = new FormData();
      formData.append('file', blob, `voice_input.${ext}`);

      const response = await axios.post(`${API}/transcribe`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.transcript && response.data.success) {
        setInput(response.data.transcript);
        toast.success('Voice transcribed! Click send or press Enter.', { icon: '🎤' });
      } else {
        toast.error('Could not transcribe audio. Please try again.');
      }
    } catch (error) {
      console.error('Whisper transcription error:', error);
      toast.error('Transcription failed. Please try again or type your question.');
    } finally {
      setTranscribingVoice(false);
    }
  };

  const toggleVoiceOutput = () => {
    if (voiceOutputEnabled) {
      stopSpeaking();
    }
    setVoiceOutputEnabled(!voiceOutputEnabled);
    toast.success(voiceOutputEnabled ? '🔇 Voice output disabled' : '🔊 Voice output enabled');
  };

  const generateStudyPlan = async () => {
    if (subjects.length === 0) {
      toast.error('Please add subjects first');
      return;
    }

    setLoading(true);
    stopSpeaking();
    
    const userMessage = { role: 'user', content: '📅 Generate a personalized study plan for me based on my current progress.' };
    setMessages(prev => [...prev, userMessage]);

    try {
      const progressRes = await axios.get(`${API}/progress`).catch(() => ({ data: {} }));
      const response = await axios.post(`${API}/study-buddy/study-plan`, null, {
        params: { 
          subject_id: subjects[0].id,
          current_level: 'intermediate',
          user_progress: JSON.stringify(progressRes.data) 
        }
      });

      const aiMessage = {
        role: 'assistant',
        content: '📚 **Your Personalized Study Plan**\n\n' + response.data.study_plan
      };
      setMessages(prev => [...prev, aiMessage]);
      
      if (voiceOutputEnabled) {
        setTimeout(() => speakText(aiMessage.content), 100);
      }
    } catch (error) {
      console.error('Error generating study plan:', error);
      toast.error('Failed to generate study plan');
    } finally {
      setLoading(false);
    }
  };

  const generateAdaptiveQuiz = async () => {
    if (subjects.length === 0) {
      toast.error('Please add subjects first');
      return;
    }

    setLoading(true);
    stopSpeaking();
    
    const userMessage = { role: 'user', content: '🎯 Create an adaptive quiz to test my knowledge.' };
    setMessages(prev => [...prev, userMessage]);

    try {
      const response = await axios.post(`${API}/study-buddy/quiz`, null, {
        params: { 
          subject_id: subjects[0].id,
          difficulty: 'medium',
          question_count: 5,
          user_performance: JSON.stringify({ recent_scores: [] }) 
        }
      });

      const aiMessage = {
        role: 'assistant',
        content: '🎯 **Adaptive Quiz**\n\n' + response.data.quiz
      };
      setMessages(prev => [...prev, aiMessage]);
      
      if (voiceOutputEnabled) {
        setTimeout(() => speakText(aiMessage.content), 100);
      }
    } catch (error) {
      console.error('Error generating quiz:', error);
      toast.error('Failed to generate quiz');
    } finally {
      setLoading(false);
    }
  };

  const generateExamStrategy = async () => {
    if (subjects.length === 0) {
      toast.error('Please add subjects first');
      return;
    }

    setLoading(true);
    stopSpeaking();
    
    const userMessage = { role: 'user', content: '🎓 Help me prepare for my upcoming exams with a study strategy.' };
    setMessages(prev => [...prev, userMessage]);

    try {
      const examsRes = await axios.get(`${API}/exams`).catch(() => ({ data: [] }));
      const upcomingExams = examsRes.data.filter(e => !e.completed && new Date(e.exam_date) >= new Date());
      
      if (upcomingExams.length === 0) {
        const aiMessage = {
          role: 'assistant',
          content: 'I don\'t see any upcoming exams scheduled. Would you like to add an exam to your schedule first? Go to the Schedule page to add your exams, and I\'ll create a personalized study strategy for you!'
        };
        setMessages(prev => [...prev, aiMessage]);
        if (voiceOutputEnabled) {
          setTimeout(() => speakText(aiMessage.content), 100);
        }
        setLoading(false);
        return;
      }

      const response = await axios.post(`${API}/study-buddy/exam-prep`, null, {
        params: { 
          subject_id: upcomingExams[0].subject_id || subjects[0].id,
          exam_date: upcomingExams[0].exam_date,
          exam_type: 'general',
          user_data: JSON.stringify({ lectures: 0 }) 
        }
      });

      const aiMessage = {
        role: 'assistant',
        content: '🎓 **Exam Preparation Strategy**\n\n' + (response.data.exam_prep || response.data.strategy || 'No strategy generated')
      };
      setMessages(prev => [...prev, aiMessage]);
      
      if (voiceOutputEnabled) {
        setTimeout(() => speakText(aiMessage.content), 100);
      }
    } catch (error) {
      console.error('Error generating exam strategy:', error);
      toast.error('Failed to generate exam strategy');
    } finally {
      setLoading(false);
    }
  };

  const clearChat = () => {
    stopSpeaking();
    setMessages([{
      role: 'assistant',
      content: 'Chat cleared! 🧹 Ready to help you with your studies. What would you like to work on?'
    }]);
    toast.success('Chat cleared');
  };

  const formatMessage = (content) => {
    // Parse inline markdown within a text string
    const parseInline = (text) => {
      const parts = [];
      let remaining = text;
      let key = 0;

      while (remaining.length > 0) {
        // Bold: **text**
        const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
        // Inline code: `text`
        const codeMatch = remaining.match(/`(.+?)`/);

        // Find earliest match
        let earliest = null;
        let matchType = null;

        if (boldMatch && (!earliest || boldMatch.index < earliest.index)) {
          earliest = boldMatch;
          matchType = 'bold';
        }
        if (codeMatch && (!earliest || codeMatch.index < earliest.index)) {
          earliest = codeMatch;
          matchType = 'code';
        }

        if (!earliest) {
          parts.push(remaining);
          break;
        }

        // Add text before the match
        if (earliest.index > 0) {
          parts.push(remaining.substring(0, earliest.index));
        }

        // Add the formatted element
        if (matchType === 'bold') {
          parts.push(<strong key={key++} className="font-semibold text-[var(--isp-heading)]">{earliest[1]}</strong>);
        } else if (matchType === 'code') {
          parts.push(<code key={key++} className="px-1.5 py-0.5 bg-white/10 rounded text-purple-300 text-sm font-mono">{earliest[1]}</code>);
        }

        remaining = remaining.substring(earliest.index + earliest[0].length);
      }

      return parts;
    };

    return content
      .split('\n')
      .map((line, i) => {
        // Headers: ### text
        if (line.startsWith('### ')) {
          return <h4 key={`h-${i}`} className="font-bold text-base text-[var(--isp-heading)] mt-3 mb-1">{parseInline(line.slice(4))}</h4>;
        }
        // Subheaders or full-line bold: **text**
        const fullBold = line.match(/^\*\*(.+)\*\*$/);
        if (fullBold) {
          return <h4 key={`h-${i}`} className="font-bold text-base text-[var(--isp-heading)] mt-3 mb-1">{fullBold[1]}</h4>;
        }
        // Bullet points (-, •, or * followed by space)
        if (line.startsWith('• ') || line.startsWith('- ') || line.match(/^\*\s/)) {
          const bulletText = line.replace(/^[•\-\*]\s/, '');
          return <li key={`li-${i}`} className="ml-4 mb-1 list-disc">{parseInline(bulletText)}</li>;
        }
        // Numbered lists
        const numMatch = line.match(/^(\d+)\.\s(.+)/);
        if (numMatch) {
          return <li key={`ol-${i}`} className="ml-4 mb-1 list-decimal">{parseInline(numMatch[2])}</li>;
        }
        // Empty lines
        if (line.trim() === '') {
          return <br key={`br-${i}`} />;
        }
        // Regular text with inline formatting
        return <p key={`p-${i}`} className="mb-1">{parseInline(line)}</p>;
      });
  };

  return (
    <div className="flex gap-0 h-[calc(100vh-5rem)] -mt-4 -mb-8" data-testid="study-buddy-page">
      {/* Chat History Sidebar */}
      <ChatSidebar
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        sessions={sessions}
        sessionsLoading={sessionsLoading}
        activeSessionId={activeSessionId}
        onNewChat={createNewChat}
        onLoadSession={loadSession}
        onDeleteSession={deleteSession}
      />

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 p-4 pb-2 flex-shrink-0">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="rounded-xl p-2 text-[var(--isp-body)] hover:text-[var(--isp-heading)] hover:bg-[var(--isp-glass-inner)]"
            data-testid="toggle-sidebar"
          >
            <PanelLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[var(--isp-heading)] tracking-tight flex items-center gap-2">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-500 flex items-center justify-center">
                <Brain className="w-5 h-5 text-white" />
              </div>
              Study Buddy AI
            </h1>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          {/* Voice Character Selector */}
          <div className="relative">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setVoiceSettingsOpen(!voiceSettingsOpen)}
              className="rounded-xl gap-2 border-[var(--isp-glass-border)] text-[var(--isp-body)] hover:text-[var(--isp-heading)] hover:bg-[var(--isp-glass-inner)]"
              data-testid="voice-character-toggle"
            >
              <span className="text-base">{VOICE_PERSONAS.find(p => p.id === selectedPersona)?.icon || '🎤'}</span>
              <span className="hidden sm:inline">{VOICE_PERSONAS.find(p => p.id === selectedPersona)?.name || 'Voice'}</span>
              <ChevronDown className={`w-3 h-3 transition-transform ${voiceSettingsOpen ? 'rotate-180' : ''}`} />
            </Button>
            
            {/* Voice Persona Dropdown */}
            <AnimatePresence>
              {voiceSettingsOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="absolute top-full right-0 mt-2 w-56 bg-slate-800 border border-[var(--isp-glass-border)] rounded-xl shadow-xl z-50 overflow-hidden"
                >
                  <div className="p-2 border-b border-[var(--isp-glass-border)]">
                    <p className="text-xs text-[var(--isp-body)] px-2">Voice Character</p>
                  </div>
                  {VOICE_PERSONAS.map((persona) => (
                    <button
                      key={persona.id}
                      onClick={() => {
                        setSelectedPersona(persona.id);
                        setVoiceSettingsOpen(false);
                        toast.success(`Voice changed to ${persona.name}`);
                      }}
                      className={`w-full px-4 py-3 flex items-center gap-3 hover:bg-[var(--isp-glass-inner)] transition-colors ${
                        selectedPersona === persona.id ? 'bg-purple-500/20' : ''
                      }`}
                      data-testid={`voice-persona-${persona.id}`}
                    >
                      <span className="text-xl">{persona.icon}</span>
                      <div className="text-left">
                        <p className="text-white text-sm font-medium">{persona.name}</p>
                        <p className="text-[var(--isp-body)] text-xs">{persona.description}</p>
                      </div>
                      {selectedPersona === persona.id && (
                        <div className="ml-auto w-2 h-2 rounded-full bg-purple-500" />
                      )}
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={toggleVoiceOutput}
            className={`rounded-xl gap-2 ${
              voiceOutputEnabled 
                ? 'bg-green-500/20 text-green-300 border-green-500/30' 
                : 'border-[var(--isp-glass-border)] text-[var(--isp-body)] hover:text-[var(--isp-heading)] hover:bg-[var(--isp-glass-inner)]'
            }`}
            data-testid="toggle-voice-output"
          >
            {voiceOutputEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
            Voice {voiceOutputEnabled ? 'On' : 'Off'}
          </Button>
          {isSpeakingNow && (
            <Button
              variant="outline"
              size="sm"
              onClick={stopSpeaking}
              className="rounded-xl gap-2 bg-red-500/20 text-red-300 border-red-500/30 animate-pulse"
              data-testid="stop-speaking"
            >
              <Square className="w-4 h-4" />
              Stop
            </Button>
          )}
          {!isSpeakingNow && messages.length > 1 && (
            <Button
              variant="outline"
              size="sm"
              onClick={replayLastMessage}
              className="rounded-xl gap-2 border-[var(--isp-glass-border)] text-[var(--isp-body)] hover:text-[var(--isp-heading)] hover:bg-[var(--isp-glass-inner)]"
              data-testid="replay-message"
            >
              <PlayCircle className="w-4 h-4" />
              Replay
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={createNewChat}
            className="rounded-xl gap-2 border-[var(--isp-glass-border)] text-[var(--isp-body)] hover:text-[var(--isp-heading)] hover:bg-[var(--isp-glass-inner)]"
            data-testid="clear-chat"
          >
            <MessageSquarePlus className="w-4 h-4" />
            New Chat
          </Button>
        </div>
      </div>

      {/* Quick action buttons */}
      <QuickActions
        onStudyPlan={generateStudyPlan}
        onQuiz={generateAdaptiveQuiz}
        onExamStrategy={generateExamStrategy}
        onExplain={() => setInput('Explain the key concepts I need to master for my current subjects')}
        loading={loading}
      />

      {/* Chat container */}
      <Card className="flex flex-col h-[500px]  bg-[var(--isp-glass)] backdrop-blur-xl border-[var(--isp-glass-border)] overflow-hidden relative">
        {/* Swipe hint indicator */}
        {swipeHint && (
          <div className={`absolute top-2 z-10 px-3 py-1.5 bg-purple-500/90 text-white text-xs rounded-full shadow-lg backdrop-blur-sm ${
            swipeHint.direction === 'left' ? 'right-2' : 'left-2'
          }`} data-testid="swipe-hint">
            {swipeHint.direction === 'right' ? '← ' : ''}{swipeHint.sessionName}{swipeHint.direction === 'left' ? ' →' : ''}
          </div>
        )}
        {/* Session indicator */}
        {sessions.length > 1 && (
          <div className="flex justify-center gap-1 py-1 sm:hidden" data-testid="session-dots">
            {sessions.slice(0, 8).map((s, i) => (
              <div key={s.id} className={`w-1.5 h-1.5 rounded-full transition-all ${s.id === activeSessionId ? 'bg-purple-400 w-3' : 'bg-white/20'}`} />
            ))}
          </div>
        )}
        {/* Messages */}
        <MessageList
          messages={messages}
          loading={loading}
          isSpeakingNow={isSpeakingNow}
          voiceOutputEnabled={voiceOutputEnabled}
          onSpeakMessage={speakText}
          onStopSpeaking={stopSpeaking}
          messagesEndRef={messagesEndRef}
          formatMessage={formatMessage}
          onTouchStart={handleChatTouchStart}
          onTouchMove={handleChatTouchMove}
          onTouchEnd={handleChatTouchEnd}
        />

        {/* Input area */}
        <div className="p-4 border-t border-[var(--isp-glass-border)] bg-slate-800/30">
          {/* Attached file indicator */}
          {/* Focus scope selector — narrows AI grounding to a single
              subject so answers are tighter (vs general knowledge across
              all lectures). Persisted to localStorage so the user's
              choice survives reloads. */}
          {subjects.length > 0 && (
            <div className="flex items-center gap-2 mb-2 px-3 py-2 rounded-lg bg-[var(--isp-glass-inner)] border border-[var(--isp-glass-border)]">
              <Target className="w-3.5 h-3.5 text-[var(--isp-muted)] shrink-0" />
              <span className="text-xs text-[var(--isp-muted)] shrink-0">Focus:</span>
              <select
                value={focusScope}
                onChange={(e) => {
                  setFocusScope(e.target.value);
                  try { localStorage.setItem('istudypro_studybuddy_focus', e.target.value); } catch { /* ignore */ }
                }}
                className="flex-1 bg-transparent text-xs text-[var(--isp-heading)] focus:outline-none cursor-pointer font-medium"
                data-testid="studybuddy-focus-scope"
              >
                <option value="all">All subjects (general knowledge)</option>
                {subjects.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
          )}
          {attachedFile && (
            <div className="flex items-center gap-2 mb-2 px-3 py-2 rounded-lg bg-indigo-500/10 border border-indigo-500/20 text-sm" data-testid="attached-file-badge">
              <FileText className="w-4 h-4 text-indigo-400" />
              <span className="text-indigo-300 truncate flex-1">{attachedFile.name}</span>
              <button onClick={() => setAttachedFile(null)} className="text-indigo-400 hover:text-indigo-200">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
          {fileAnalyzing && (
            <div className="flex items-center gap-2 mb-2 px-3 py-2 rounded-lg bg-purple-500/10 border border-purple-500/20 text-sm text-purple-300">
              <Loader2 className="w-4 h-4 animate-spin" /> Analyzing document...
            </div>
          )}
          <div className="flex flex-col gap-2">
            <Textarea
              placeholder="Ask me anything... I can help with studying, create quizzes, explain concepts, and more!"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              className="rounded-xl bg-[var(--isp-glass-inner)] border-[var(--isp-glass-border)] text-[var(--isp-heading)] placeholder:text-[var(--isp-muted)] resize-none min-h-[60px]"
              data-testid="study-buddy-input"
            />
            <div className="flex items-center gap-2">
              <input
                type="file"
                ref={fileInputRef}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) setAttachedFile(f);
                  e.target.value = '';
                }}
                accept=".pdf,.txt,.doc,.docx"
                className="hidden"
                data-testid="file-upload-input"
              />
              <Button
                size="icon"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={loading || fileAnalyzing}
                className="rounded-xl border-[var(--isp-glass-border)] text-[var(--isp-body)] hover:text-[var(--isp-heading)] hover:bg-[var(--isp-glass-inner)] h-9 w-9 shrink-0"
                data-testid="attach-file-button"
                title="Attach document (PDF, TXT, DOC)"
              >
                <Paperclip className="w-4 h-4" />
              </Button>
              <Button
                size="icon"
                variant={recording ? 'destructive' : 'outline'}
                onClick={handleVoice}
                disabled={!voiceInputEnabled || transcribingVoice}
                className={`rounded-xl h-9 w-9 shrink-0 ${
                  transcribingVoice
                    ? 'bg-purple-500/20 border-purple-500/30 text-purple-400'
                    : recording 
                      ? 'bg-red-500 hover:bg-red-600 animate-pulse' 
                      : 'border-[var(--isp-glass-border)] text-[var(--isp-body)] hover:text-[var(--isp-heading)] hover:bg-[var(--isp-glass-inner)]'
                }`}
                data-testid="voice-button"
                title={
                  transcribingVoice ? 'Transcribing...' :
                  recording ? 'Click to stop recording' :
                  voiceInputEnabled ? 'Click to speak' : 'Voice not supported'
                }
              >
                {transcribingVoice ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : recording ? (
                  <MicOff className="w-4 h-4" />
                ) : (
                  <Mic className="w-4 h-4" />
                )}
              </Button>
              <div className="flex-1" />
              {voiceInputEnabled && (
                <p className="text-[10px] text-green-400 flex items-center gap-1 shrink-0">
                  <Mic className="w-3 h-3" />
                  Voice {voiceMode === 'whisper' ? '(Whisper)' : ''}
                </p>
              )}
              {transcribingVoice && (
                <p className="text-[10px] text-purple-400 flex items-center gap-1 shrink-0">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Transcribing...
                </p>
              )}
              <Button aria-label="Send"
                size="icon"
                onClick={handleSend}
                disabled={(!input.trim() && !attachedFile) || loading || fileAnalyzing}
                className="rounded-xl bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 disabled:opacity-50 h-9 w-9 shrink-0"
                data-testid="send-button"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <p className="text-[10px] text-[var(--isp-muted)] mt-1 hidden sm:block">
            Press Enter to send, Shift+Enter for new line
          </p>
        </div>
      </Card>
      </div> {/* End Main Chat Area */}

      {/* First-visit personalization capture — fires once per user, gated by
          localStorage flag inside the modal. */}
      <Day0TutorMemoryModal />
    </div>
  );
};

export default StudyBuddyPage;
